import {
  Layout,
  Menu,
  Popconfirm,
  Dropdown,
  Button,
  Grid,
} from 'antd'

import {
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  OrderedListOutlined,
  SafetyCertificateOutlined,
  AreaChartOutlined,
  LineChartOutlined,
  MenuOutlined,
  LockOutlined,
  AppstoreOutlined,
  DownOutlined,
  ShoppingOutlined,
  ScanOutlined,
  HistoryOutlined,
} from '@ant-design/icons'

import './index.scss'

import React, {
  useMemo,
  useState,
} from 'react'

import {
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { CombinedLogo } from '@/components/Logo'
import { products } from '@/config/products'

const { Header, Sider, Content } = Layout
const { useBreakpoint } = Grid

// ======================================================
// Icon Map
// ======================================================

const iconMap = {
  LineChartOutlined,
  AreaChartOutlined,
  SafetyCertificateOutlined,
  HomeOutlined,
  UserOutlined,
  AppstoreOutlined,
}

const getProductIcon = (iconName) => {
  const IconComponent =
    iconMap[iconName] || AppstoreOutlined

  return <IconComponent />
}

// ======================================================
// Lottery Module
//
// Lottery modules use submenu navigation.
//
// Current:
//   649
//
// Future:
//   max
// ======================================================

const isLotteryModule = (module) => {

  if (!module) {
    return false
  }

  return (
    module.key === '649' ||
    module.key === 'max'
  )
}

// ======================================================
// Build Lottery Children
// ======================================================

const buildLotteryChildren = (module) => {

  const basePath =
    module.path

  return [

    {
      label: 'Dashboard',

      key: basePath,

      icon:
        <LineChartOutlined />,

    },

    {
      label: 'My Tickets',

      key:
        `${basePath}/my-tickets`,

      icon:
        <ShoppingOutlined />,
    },

    {
      label: 'Draw History',

      key:
        `${basePath}/draw-history`,

      icon:
        <HistoryOutlined />,

    },

    {
      label: 'Scan Ticket',

      key:
        `${basePath}/scan-ticket`,

      icon:
        <ScanOutlined />,

    },

    {
      label: 'Statistics',

      key:
        `${basePath}/statistics`,

      icon:
        <AreaChartOutlined />,

    },

  ]
}

// ======================================================
// Normalize Role
//
// Supported:
// Administrator
// administrator
// Admin
// admin
// OM
// TM
// Staff
// ======================================================

const normalizeRole = (user) => {

  if (!user) {
    return null
  }

  const roles = []

  // ----------------------------------------------------
  // Single role
  // ----------------------------------------------------

  if (user.role) {

    roles.push(
      String(user.role)
        .trim()
        .toLowerCase()
    )

  }

  // ----------------------------------------------------
  // Multiple roles
  // ----------------------------------------------------

  if (Array.isArray(user.roles)) {

    user.roles.forEach((item) => {

      if (typeof item === 'string') {

        roles.push(
          item
            .trim()
            .toLowerCase()
        )

      } else if (item?.name) {

        roles.push(
          String(item.name)
            .trim()
            .toLowerCase()
        )

      } else if (item?.role) {

        roles.push(
          String(item.role)
            .trim()
            .toLowerCase()
        )

      }

    })

  }

  // ----------------------------------------------------
  // Administrator
  // ----------------------------------------------------

  if (
    roles.includes('administrator') ||
    roles.includes('admin')
  ) {

    return 'Administrator'

  }

  // ----------------------------------------------------
  // OM
  // ----------------------------------------------------

  if (roles.includes('om')) {
    return 'OM'
  }

  // ----------------------------------------------------
  // TM
  // ----------------------------------------------------

  if (roles.includes('tm')) {
    return 'TM'
  }

  // ----------------------------------------------------
  // Staff
  // ----------------------------------------------------

  if (roles.includes('staff')) {
    return 'Staff'
  }

  return null
}

// ======================================================
// Purchased Products
// ======================================================

const getPurchasedProducts = (user) => {

  if (!user) {
    return []
  }

  return Array.isArray(user.purchasedProducts)
    ? user.purchasedProducts
    : []
}

// ======================================================
// Module Access
//
// Access rules:
//
// Administrator
//   -> Full access
//
// OM
//   -> Full access
//
// All other authenticated users
//   -> Access only to subscribed modules
// ======================================================

const canAccessModule = ({
  module,
  role,
  purchasedProducts,
}) => {

  // ----------------------------------------------------
  // Invalid module
  // ----------------------------------------------------

  if (!module) {
    return false
  }

  // ----------------------------------------------------
  // Coming Soon
  // ----------------------------------------------------

  if (
    module.status === 'coming_soon'
  ) {

    return false

  }

  // ----------------------------------------------------
  // Administrator
  // ----------------------------------------------------

  if (
    role === 'Administrator'
  ) {

    return true

  }

  // ----------------------------------------------------
  // OM
  // ----------------------------------------------------

  if (
    role === 'OM'
  ) {

    return true

  }

  // ----------------------------------------------------
  // Purchased module access
  // ----------------------------------------------------

  return purchasedProducts.includes(
    module.key
  )
}

// ======================================================
// Lottery Child Access
//
// Children inherit the module's access.
// This function exists mainly to keep the structure
// clear and ready for future module-level rules.
// ======================================================

const buildLotteryChildrenWithAccess = ({
  module,
  hasAccess,
}) => {

  const children =
    buildLotteryChildren(module)

  return children.map((item) => ({
    ...item,

    disabled:
      !hasAccess,

  }))
}

// ======================================================
// Build Navigation
// ======================================================

const buildNavigationItems = ({
  role,
  purchasedProducts,
}) => {

  const navigation = []

  // ====================================================
  // 1. Home
  // ====================================================

  navigation.push({

    label: 'Home',

    key: '/',

    icon:
      <HomeOutlined />,

  })

  // ====================================================
  // 2. Products
  // ====================================================

  products.forEach((product) => {

    if (
      !product.modules ||
      product.modules.length === 0
    ) {

      return

    }

    const moduleItems =
      product.modules.map((module) => {

        const isComingSoon =
          module.status === 'coming_soon'

        const hasAccess =
          canAccessModule({
            module,
            role,
            purchasedProducts,
          })

        // ==================================================
        // Lottery Module
        //
        // Example:
        //
        // Lotto 6/49
        //   Dashboard
        //   My Tickets
        //   Draw History
        //   Scan Ticket
        //   Statistics
        //
        // Future:
        //
        // Lotto MAX
        //   Dashboard
        //   My Tickets
        //   ...
        // ==================================================

        if (
          isLotteryModule(module) &&
          !isComingSoon
        ) {

          return {

            label: (

              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: '8px',
                }}
              >

                <span>
                  {module.name}
                </span>

              </span>

            ),

            key:
              module.path,

            icon:
              getProductIcon(module.icon),

            disabled:
              !hasAccess,

            productKey:
              product.key,

            moduleKey:
              module.key,

            children:
              buildLotteryChildrenWithAccess({
                module,
                hasAccess,
              }),

          }

        }

        // ==================================================
        // Normal Module
        // ==================================================

        return {

          label: (

            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: '8px',
              }}
            >

              <span>
                {module.name}
              </span>

              {isComingSoon && (

                <LockOutlined
                  style={{
                    fontSize: '12px',
                    opacity: 0.6,
                  }}
                />

              )}

            </span>

          ),

          key:
            module.path,

          icon:
            getProductIcon(module.icon),

          disabled:
            isComingSoon ||
            !hasAccess,

          productKey:
            product.key,

          moduleKey:
            module.key,

        }

      })

    // ==================================================
    // Free Roulette Game
    //
    // Always available to authenticated users.
    //
    // This is NOT a paid module.
    // ==================================================

    const isGamingGroup =
      product.key?.toLowerCase() === 'gaming' ||
      product.name?.toLowerCase() === 'gaming'

    if (isGamingGroup) {

      moduleItems.unshift({

        label: (

          <span>
            Roulette Game (Free)
          </span>

        ),

        key:
          '/roulette-game',

        icon:
          <AppstoreOutlined />,

        disabled:
          false,

        productKey:
          product.key,

        moduleKey:
          'roulette-game',

      })

    }

    if (
      moduleItems.length === 0
    ) {

      return

    }

    navigation.push({

      type: 'group',

      label:
        product.name,

      key:
        `product-${product.key}`,

      children:
        moduleItems,

    })

  })

  // ====================================================
  // 3. Account
  // ====================================================

  navigation.push({

    type: 'group',

    label:
      'Account',

    key:
      'account',

    children: [

      {

        label:
          'Profile',

        key:
          '/profile',

        icon:
          <UserOutlined />,

      },

    ],

  })

  // ====================================================
  // 4. Administration
  //
  // Administrator + OM
  // ====================================================

  if (
    role === 'Administrator' ||
    role === 'OM'
  ) {

    navigation.push({

      type: 'group',

      label:
        'Administration',

      key:
        'administration',

      children: [

        {

          label:
            'Lottery Draw Management',

          key:
            '/lottery-draws',

          icon:
            <LineChartOutlined />,

        },

        {

          label:
            'Staff Management',

          key:
            '/users',

          icon:
            <UserOutlined />,

        },

      ],

    })

  }

  return navigation
}

// ======================================================
// GeekLayout
// ======================================================

const GeekLayout = () => {

  const navigate =
    useNavigate()

  const location =
    useLocation()

  const {
    user,
    loading,
    logout,
  } = useAuth()

  const screens =
    useBreakpoint()

  const isMobile =
    !screens.md

  const [
    mobileMenuOpen,
    setMobileMenuOpen,
  ] = useState(false)

  // ====================================================
  // Role
  // ====================================================

  const role =
    normalizeRole(user)

  // ====================================================
  // Purchased Products
  // ====================================================

  const purchasedProducts =
    getPurchasedProducts(user)

  // ====================================================
  // RBAC Debug
  // ====================================================

  console.log(
    '===== RBAC DEBUG ====='
  )

  console.log(
    'Auth user:',
    user
  )

  console.log(
    'Auth user.role:',
    user?.role
  )

  console.log(
    'Auth user.roles:',
    user?.roles
  )

  console.log(
    'Normalized role:',
    role
  )

  console.log(
    'Purchased products:',
    purchasedProducts
  )

  console.log(
    '====================='
  )

  // ====================================================
  // Display Name
  // ====================================================

  const displayName =
    user?.fullName ||
    (
      user?.firstName
        ? `${user.firstName} ${user.lastName || ''}`.trim()
        : null
    ) ||
    user?.email ||
    'User'

  // ====================================================
  // Navigation
  // ====================================================

  const menuItems =
    useMemo(
      () =>
        buildNavigationItems({
          role,
          purchasedProducts,
        }),
      [
        role,
        purchasedProducts,
      ]
    )

  // ====================================================
  // Menu Click
  // ====================================================

  const onMenuClick = ({
    key,
  }) => {

    setMobileMenuOpen(false)

    // ==================================================
    // Lottery Child Routes
    //
    // These routes are children of the Lottery submenu.
    //
    // IMPORTANT:
    // They are not module.path themselves, so they must
    // be handled before looking for targetModule.
    // ==================================================

    const lotteryChildPaths = new Set([

      '/lotto-649',

      '/lotto-649/my-tickets',

      '/lotto-649/draw-history',

      '/lotto-649/scan-ticket',

      '/lotto-649/statistics',

      '/lotto-max',

      '/lotto-max/my-tickets',

      '/lotto-max/draw-history',

      '/lotto-max/scan-ticket',

      '/lotto-max/statistics',

    ])

    if (
      lotteryChildPaths.has(key)
    ) {

      // ------------------------------------------------
      // Determine which parent lottery module this route
      // belongs to.
      // ------------------------------------------------

      const lotteryModule =
        products
          .flatMap(
            (product) =>
              product.modules || []
          )
          .find((module) => {

            if (
              !isLotteryModule(module)
            ) {

              return false

            }

            return (
              key === module.path ||
              key.startsWith(
                `${module.path}/`
              )
            )

          })

      // ------------------------------------------------
      // Respect module access
      // ------------------------------------------------

      if (
        lotteryModule
      ) {

        const hasAccess =
          canAccessModule({

            module:
              lotteryModule,

            role,

            purchasedProducts,

          })

        if (!hasAccess) {

          return

        }

      }

      // ------------------------------------------------
      // Navigate
      // ------------------------------------------------

      navigate(key)

      return

    }

    // ==================================================
    // Find Product Module
    // ==================================================

    const allModules =
      products.flatMap(
        (product) =>
          product.modules || []
      )

    const targetModule =
      allModules.find(
        (module) =>
          module.path === key
      )

    // ==================================================
    // Free Roulette Game
    //
    // It is intentionally NOT in products.modules.
    // Therefore it will be handled as a normal route.
    // ==================================================

    if (
      key === '/roulette-game'
    ) {

      navigate(key)

      return

    }

    // ==================================================
    // Coming Soon
    // ==================================================

    if (
      targetModule?.status ===
      'coming_soon'
    ) {

      return

    }

    // ==================================================
    // Normal Router Pages
    //
    // Home
    // Profile
    // Administration
    // etc.
    // ==================================================

    if (!targetModule) {

      navigate(key)

      return

    }

    // ==================================================
    // Product Access
    // ==================================================

    const hasAccess =
      canAccessModule({

        module:
          targetModule,

        role,

        purchasedProducts,

      })

    if (!hasAccess) {

      return

    }

    // ==================================================
    // TM Desktop
    //
    // Existing behavior preserved.
    // ==================================================

    if (
      role === 'TM' &&
      !isMobile
    ) {

      const width =
        500

      const height =
        896

      const left =
        (
          window.screen.width -
          width
        ) / 2

      const top =
        (
          window.screen.height -
          height
        ) / 2

      window.open(

        key,

        `AnalyticsWindow_${key.replace('/', '')}`,

        [
          `width=${width}`,
          `height=${height}`,
          `top=${top}`,
          `left=${left}`,
          'resizable=yes',
          'scrollbars=yes',
        ].join(',')

      )

      return

    }

    // ==================================================
    // Navigate
    // ==================================================

    navigate(key)

  }

  // ====================================================
  // Selected Menu Key
  // ====================================================

  let menuKey =
    location.pathname

  // ----------------------------------------------------
  // Legacy JobBid route handling
  //
  // Kept temporarily so existing routes do not break.
  // ----------------------------------------------------

  if (
    menuKey.includes(
      '/bid-config/'
    )
  ) {

    menuKey =
      '/bid'

  }

  // ====================================================
  // Logout
  // ====================================================

  const onConfirm = async () => {

    try {

      await logout()

    } catch (error) {

      console.error(
        'Logout failed:',
        error
      )

    }

    navigate(
      '/login',
      {
        replace: true,
      }
    )

  }

  // ====================================================
  // Render Menu
  // ====================================================

  const renderMenu = () => (

    <Menu

      mode="inline"

      theme="dark"

      selectedKeys={[
        menuKey,
      ]}

      onClick={
        onMenuClick
      }

      items={
        menuItems
      }

      className="custom-app-menu"

    />

  )

  // ====================================================
  // Render
  // ====================================================

  return (

    <Layout
      className="app-geek-layout"
    >

      {/* ==================================================
          Header
          ================================================== */}

      <Header
        className="app-header"
      >

        {/* Left */}

        <div
          className="header-left"
        >

          {isMobile ? (

            <Dropdown

              dropdownRender={() => (

                <div
                  className="mobile-dropdown-wrapper"
                >

                  <Menu

                    mode="inline"

                    theme="dark"

                    selectedKeys={[
                      menuKey,
                    ]}

                    onClick={
                      onMenuClick
                    }

                    items={
                      menuItems
                    }

                    className="custom-app-menu"

                  />

                </div>

              )}

              trigger={[
                'click',
              ]}

              open={
                mobileMenuOpen
              }

              onOpenChange={
                (flag) =>
                  setMobileMenuOpen(flag)
              }

              placement="bottomLeft"

              getPopupContainer={
                (triggerNode) =>
                  triggerNode.parentNode
              }

            >

              <Button
                type="text"
                className="mobile-menu-btn"
              >

                <MenuOutlined
                  style={{
                    fontSize: '18px',
                    color: '#38bdf8',
                  }}
                />

                <span
                  className="mobile-menu-text"
                >
                  Menu
                </span>

                <DownOutlined
                  style={{
                    fontSize: '10px',
                    color: '#94a3b8',
                  }}
                />

              </Button>

            </Dropdown>

          ) : (

            <div
              className="brand-logo-area"
            >

              <OrderedListOutlined
                className="brand-icon"
              />

              <span
                className="brand-title"
              >
                Lotto Analytics
              </span>

            </div>

          )}

        </div>

        {/* ==================================================
            Center Logo
            ================================================== */}

        <div
          className="header-center"
          onClick={() =>
            navigate('/')
          }
        >

          <CombinedLogo
            height={28}
          />

        </div>

        {/* ==================================================
            Right
            ================================================== */}

        <div
          className="header-right"
        >

          <span
            className="user-name"
          >
            {displayName}
          </span>

          <Popconfirm

            title="Confirm to exit?"

            okText="Exit"

            cancelText="Cancel"

            onConfirm={
              onConfirm
            }

          >

            <Button
              type="text"
              danger
              className="logout-btn"
            >

              <LogoutOutlined />

              {!isMobile && (

                <span>
                  Exit
                </span>

              )}

            </Button>

          </Popconfirm>

        </div>

      </Header>

      {/* ==================================================
          Body
          ================================================== */}

      <Layout
        className="app-body"
      >

        {/* ==================================================
            Desktop Sidebar
            ================================================== */}

        {!isMobile && (

          <Sider
            width={210}
            className="app-sider"
          >

            {renderMenu()}

          </Sider>

        )}

        {/* ==================================================
            Main Content
            ================================================== */}

        <Content
          className="app-content-container"
        >

          <Outlet />

        </Content>

      </Layout>

    </Layout>

  )

}

export default GeekLayout